import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getTotalSelectedProducts, isDependentProductSelected } from '../../product-picker/selectors/products';
import { createReservation, updateQuantities } from '../../../actions/reservation';
import { createLoadingSelector } from '../../../selectors/helpers';
import { getTotalSelectedItems, getProductErrors, getBundleErrors } from '../selectors/selected-items';
import Button from '@checkout-s/Button';
import { setDisplayConfigurationError } from '../actions/ticket-picker';
import { reservationActive } from '../../../selectors/reservation';
import { isKioskMode } from 'checkout/selectors/ui';
import {
  showBestAvailablePicker,
  numberSelectedBestAvailableSeats,
  showPyosPicker,
  getSectionPriceLevelsWithUnmetMinimums,
  getSectionPriceLevelsWithExceededMaximums
} from 'checkout/selectors/reserved-seating';
import { getPriceLevelsWithUnmetMinimums } from '../selectors/tickets';
import { addAlert } from '../../../actions/ui.js';
import Translation from '@checkout-s/Translation';
import { getReserveCouponUsage, isSeatedListing } from '../../../selectors/reserved-seating';
import { isMultiEmbed } from '../../../selectors/ui.js';
import { isDynamicListing } from '../../../selectors/dynamic-listing';
import { getIsTicketPickerAdditive } from '../selectors/ticket-picker';
import { withRouterCompat } from "../../../helpers/withRouterCompat";

class CheckoutButton extends Component {
  buttonText = () => {
    const { showPyosPicker, showBestAvailablePicker, isMultiEmbed, currentRouteName } = this.props;

    const showAddToCartText =
      isMultiEmbed &&
      currentRouteName !== 'tickets-reserved' &&
      (currentRouteName === 'picker-with-details' || showPyosPicker || showBestAvailablePicker);

    if (this.props.isKioskMode) {
      return <Translation>{'checkout__checkout'}</Translation>;
    }
    if (this.props.showBestAvailablePicker && !this.props.hasReserveCoupon) {
      return <Translation>{'checkout__find_seats'}</Translation>;
    }
    if (showAddToCartText || this.props.isDynamicListing) {
      return <Translation>{'checkout__add_to_cart'}</Translation>
    }
    return <Translation>{'checkout__proceed_to_checkout'}</Translation>;
  };

  disabled = () => {
    if (this.props.maintenanceMode) {
      return true;
    }
    if (this.props.currentRouteName === 'tickets-reserved') {
      return false;
    }
    if (this.props.showPyosPicker && this.props.pyosLoading) {
      return true;
    }
    if (
      !this.props.showBestAvailablePicker &&
      !this.props.showPyosPicker &&
      !this.props.selectedItems &&
      !this.props.selectedProducts
    ) {
      return true;
    }
    if (!!this.props.showBestAvailablePicker && this.props.numberSelectedBestAvailableSeats < 1) {
      return true;
    }
    if (this.props.showPyosPicker && (!this.props.ticketCount || +this.props.ticketCount < 1 || this.props.hasErrorAlerts)) {
      return true;
    }
    if (this.props.showPyosPicker && this.props.pyosSelecting) {
      return true;
    }
    if (!this.props.showBestAvailablePicker && this.dependentProductError()) {
      return true;
    }
    return false;
  };

  render() {
    const buttonClass = this.props.isKioskMode
      ? 'button--primary button--large kiosk-footer__button--primary'
      : 'button--primary button--large add-to-cart__button';

    return (
      <div className="add-to-cart">
        <Button
          onClick={this.requestInventory}
          className={buttonClass}
          disabled={this.disabled()}
          loading={this.props.pyosLoading  || this.props.loading || this.props.taggableCouponLoading}
          role="link"
          data-href="#checkout"
          aria-describedby={this.props.isSeated ? "seated-tickets-body" : undefined}
        >
          {this.buttonText()}
        </Button>
      </div>
    );
  }

  dependentProductError = () => {
    return (
      (this.props.ticketCount < 1 || this.props.ticketCount === undefined) &&
      (this.props.bundleCount < 1 || this.props.bundleCount === undefined) &&
      (!this.props.selectedItems && this.props.selectedDependentProducts)
    );
  };

  alertUnmetMin = (name, min) => {
    this.props.addAlert('checkout__must_purchase_n_tickets_to_pay_price', 'error', true, { name, min });
  };

  alertExceededMax = (name, max) => {
    this.props.addAlert('checkout__must_purchase_less_than_n_tickets_to_pay_price', 'error', true, { name, max });
  };

  requestInventory = () => {
    const {
      currentRouteName,
      token,
      isMultiEmbed,
      showPyosPicker,
      showBestAvailablePicker,
      navigate
    } = this.props;

    if (currentRouteName === 'tickets-reserved') {
      navigate(`/checkout/${token}`);
      return false;
    }

    const productsValid = Object.keys(this.props.productsWithErrors).length === 0;
    const bundlesValid = Object.keys(this.props.bundleProductsWithErrors).length === 0;

    // only checking this if pyos because other ticket pickers should only allow selecting valid quantities
    if (this.props.showPyosPicker) {
      if (this.props.sectionPriceLevelsWithUnmetMinimums.length) {
        this.props.sectionPriceLevelsWithUnmetMinimums.forEach(secLevel => {
          this.alertUnmetMin(secLevel.name, secLevel.minimum_purchase_limit);
        });
        return false;
      }
      if (this.props.sectionPriceLevelsWithExceededMaximums.length) {
        this.props.sectionPriceLevelsWithExceededMaximums.forEach(secLevel => {
          this.alertExceededMax(secLevel.name, secLevel.maximum_purchase_limit);
        });
        return false;
      }
      if (this.props.priceLevelsWithUnmetMinimums.length) {
        this.props.priceLevelsWithUnmetMinimums.forEach(lvl => {
          this.alertUnmetMin(lvl.name, lvl.minimum_purchase_limit);
        });
        return false;
      }
    }

    if (!productsValid || !bundlesValid) {
      this.props.setDisplayConfigurationError(true);
      return false;
    } else {
      this.props.setDisplayConfigurationError(false);
    }

    const shouldGoToTicketReservedStep =
      isMultiEmbed &&
      (currentRouteName === 'picker-with-details' || showPyosPicker || showBestAvailablePicker);

    let to = shouldGoToTicketReservedStep ? 'tickets-reserved' : 'checkout';

    if (this.props.isDynamicListing) {
      to = 'continue-shopping';
    }

    if (!this.props.reservationActive) {
      this.props.createReservation(true, to);
    } else {
      this.props.updateQuantities(null, null, this.props.updateQuantitiesOptions, null, to);
    }
  };
}

const getReservationLoading = createLoadingSelector(['RESERVATION_CREATE', 'RESERVATION_FETCH']);
const mapStateToProps = (state, props) => {
  /**
   * If this is a dynamic listing, then ticketpicker state will not be retained.
   * If the user visits the same ticketpicker again after carting tickets, treat
   * their selection as additive.
   */
  let updateQuantitiesOptions = null;
  if (getIsTicketPickerAdditive(state)) {
    const ticketQuantities = {...state.ticketPicker.quantities};
    if (Object.keys(ticketQuantities).length) {
      Object.keys(ticketQuantities).forEach(levelId => {
        state.tickets.tickets.forEach(ticket => {
          if (ticket.price_level_id == levelId) {
            ticketQuantities[levelId]++;
          }
        })
      });
      updateQuantitiesOptions = {ticketQuantities};
    }
  }

  const hasErrorAlerts = state.ui.alerts.some(alert => alert.type === 'error');

  return {
    isMultiEmbed: isMultiEmbed(state),
    hasReserveCoupon: !!getReserveCouponUsage(state)?.attributes?.coupon_link_id,
    ticketCount: state.reservation.ticket_count,
    bundleCount: state.reservation.bundle_count,
    selectedItems: getTotalSelectedItems(state),
    selectedDependentProducts: isDependentProductSelected(state),
    selectedProducts: getTotalSelectedProducts(state),
    loading: getReservationLoading(state),
    productsWithErrors: getProductErrors(state),
    bundleProductsWithErrors: getBundleErrors(state),
    reservationActive: reservationActive(state),
    isKioskMode: isKioskMode(state),
    maintenanceMode: state.ui.maintenanceMode,
    showBestAvailablePicker: showBestAvailablePicker(state),
    showPyosPicker: showPyosPicker(state),
    pyosLoading: state.reservedSeating.pyosLoading,
    pyosSelecting: state.reservedSeating.pyosSelecting,
    numberSelectedBestAvailableSeats: numberSelectedBestAvailableSeats(state),
    sectionPriceLevelsWithUnmetMinimums: getSectionPriceLevelsWithUnmetMinimums(state),
    sectionPriceLevelsWithExceededMaximums: getSectionPriceLevelsWithExceededMaximums(state),
    priceLevelsWithUnmetMinimums: getPriceLevelsWithUnmetMinimums(state),
    taggableCouponLoading: state.couponTag.taggableCouponLoading,
    currentRouteName: state.ui.currentRouteName,
    token: state.reservation.token,
    isDynamicListing: isDynamicListing(state),
    isSeated: isSeatedListing(state),
    updateQuantitiesOptions,
    hasErrorAlerts,
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      createReservation,
      updateQuantities,
      setDisplayConfigurationError,
      addAlert,
    },
    dispatch
  );
};

CheckoutButton.propTypes = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouterCompat(CheckoutButton));
